import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { styled } from "@mui/material/styles";

export type EmtMarker = {
  id: string | number;
  /**
   * [lng, lat]
   */
  coordinates: [number, number];
  component: React.ReactElement;
  /**
   * @default false
   */
  isActive?: boolean;
};

export type MarkerList = Array<EmtMarker>;

export type EmtMapProps = React.ComponentPropsWithRef<any> & {
  disableNextCenterAnimation?: boolean;
  onFlyToEnd?: (center: { lng: number; lat: number }) => void;
  resizeTrigger?: number | string; // A value that changes to trigger the resize
  centerTrigger?: number | string; // A value that changes to trigger the center
  /**
   * MapBox access token
   */
  accessToken: string;
  /**
   * Marker list
   * @default []
   */
  markerList?: MarkerList;
  /**
   * Home marker, simple react element
   * @default undefined
   */
  homeMarker?: React.ReactElement;
  /**
   * Function when search button is trigger
   * @default undefined
   */
  onMapDragEnd?: (lng: number, lat: number) => void;
  /**
   * Function when zoom is trigger
   * @default undefined
   */
  onMapZoomEnd?: (
    zoom: number,
    coordinates: { lng: number; lat: number }
  ) => void;
  /**
   * Longitude
   * @default 1.444209
   */
  lng?: number;
  /**
   * Latitude
   * @default 43.604652
   */
  lat?: number;
  /**
   * Max Zoom
   * @default undefined
   */
  maxZoom?: number;
  /**
   * Min Zoom
   * @default undefined
   */
  minZoom?: number;
  /**
   * Zoom level
   * @default 14
   */
  zoom?: number;
  /**
   * Function to change value of lat before "flyto" function
   * @default undefined
   * @example (lat) => lat - 0.004
   */
  latFlyTo?: (lat: number) => number;
  onMarkerMouseEnter?: (id: number | string) => void;
  onMarkerMouseLeave?: (id: number | string) => void;
  navigationControl?: boolean;
  nonce?: string;
};

const MapBox = styled("div")(() => ({
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  "& .mapboxgl-ctrl-logo": {
    display: "none !important",
  },
  "& .marker-container": {
    cursor: "pointer",
    "& .img-marker": {
      position: "relative",
    },
    "& .img-marker.active-icon": {
      zIndex: 2,
    },
    "& .img-marker.inactive-icon": {
      zIndex: 1,
    },
    "&.active": {
      zIndex: 2,
    },
    "& .img-marker.hidden": {
      display: "none",
    },
  },
}));

const roundLatLng = (n: number | undefined) =>
  n ? parseFloat(n.toFixed(4)) : 0;

export const EmtMap = (props: EmtMapProps) => {
  const {
    homeMarker,
    markerList = [],
    accessToken,
    onMapDragEnd,
    onMapZoomEnd,
    latFlyTo,
    navigationControl = false,
    maxZoom,
    minZoom,
  } = props;
  const mapContainer = useRef(null);
  const map = useRef<mapboxgl.Map | null>(null);

  const [coordinates, setCoordinates] = useState<{
    lng: number;
    lat: number;
  }>({
    lng: props.lng ? roundLatLng(props.lng) : 1.4442,
    lat: props.lat ? roundLatLng(props.lat) : 43.6046,
  });
  const lng = coordinates.lng;
  const lat = coordinates.lat;
  const [zoom, setZoom] = useState(props.zoom ?? 14);

  useEffect(() => {
    if (!map.current || !props.zoom) return;
    map.current.zoomTo(props.zoom);
  }, [props.zoom]);

  const [currentMarkers, setCurrentMarkers] = useState<Array<any>>([]);
  const [currentMarkersDiv, setCurrentMarkersDiv] = useState<
    Array<HTMLElement>
  >([]);

  const isMounted = useRef(true);

  /**
   * Initialize all the map
   * ⬇️ [1]
   */
  useEffect(() => {
    if (!isMounted.current) return;
    if (map.current) return; // initialize map only once
    mapboxgl.accessToken = accessToken;
    map.current = new mapboxgl.Map({
      container: mapContainer.current ? mapContainer.current : "",
      style: "mapbox://styles/mapbox/streets-v11?optimize=true",
      center: [lng, latFlyTo ? latFlyTo(lat) : lat],
      zoom,
      maxZoom,
      minZoom,
      attributionControl: false,
    });

    navigationControl
      ? map.current.addControl(new mapboxgl.NavigationControl())
      : null;

    map.current.on("move", () => {
      if (map.current) {
        setCoordinates({
          lng: roundLatLng(map.current.getCenter().lng),
          lat: roundLatLng(map.current.getCenter().lat),
        });
        setZoom(map.current.getZoom());
      }
    });

    if (onMapDragEnd) {
      map.current.on("dragend", () => {
        if (map.current) {
          onMapDragEnd(
            roundLatLng(map.current.getCenter().lng),
            roundLatLng(map.current.getCenter().lat)
          );
        }
      });
    }

    if (onMapZoomEnd) {
      map.current.on("zoomend", () => {
        if (map.current) {
          onMapZoomEnd(map.current.getZoom(), {
            lng: parseFloat(map.current.getCenter().lng.toFixed(4)),
            lat: parseFloat(map.current.getCenter().lat.toFixed(4)),
          });
        }
      });
    }

    if (homeMarker) {
      const homeMarkerDiv = document.createElement("div");
      createRoot(homeMarkerDiv).render(homeMarker);
      // Add home marker to the map
      new mapboxgl.Marker(homeMarkerDiv)
        .setLngLat([lng as number, lat as number])
        .addTo(map.current);
    }

    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * On markerList props change
   * This is trigger when the props markerList is changed
   * ⬇️ [2]
   */
  useEffect(() => {
    //if (!isMounted.current && currentMarkers.length) return

    /**
     * Delete old markers from map
     */
    currentMarkers.forEach((marker) => marker.remove());
    setCurrentMarkers([]);

    /**
     * Generate DOM for markers
     * - at the end, it use ReactDOM render function
     */
    setCurrentMarkersDiv(
      markerList.map((marker: EmtMarker) => {
        const markerDiv = document.createElement("div"); // Create virtual div
        markerDiv.classList.add("marker-container"); // Add container class (it's use only for styling inside styled 'MapBox')
        markerDiv.id = marker.id as string; // Add container class (it's use only for styling inside styled 'MapBox')
        marker.isActive ? markerDiv.classList.add("active") : null; // If marker isActive, add 'active' class

        createRoot(markerDiv).render(marker.component as any);

        new mapboxgl.Marker(markerDiv)
          .setLngLat(marker.coordinates as [number, number])
          .addTo(map.current as mapboxgl.Map); // Add new marker to the map
        // Store all marker inside state, this way we can edit or delete marker inside mapBox
        setCurrentMarkers((old) => [
          ...old,
          new mapboxgl.Marker(markerDiv)
            .setLngLat(marker.coordinates as [number, number])
            .addTo(map.current as mapboxgl.Map),
        ]);

        return markerDiv;
      })
    );
  }, [markerList]);

  /**
   * On currentMarkersDiv state change
   * This is trigger when the state currentMarkersDiv is changed
   * ⬇️ [3]
   */
  useEffect(() => {
    if (!isMounted.current) return;
    if (!currentMarkersDiv.length) return;
    currentMarkersDiv.forEach((marker) => {
      marker.addEventListener("click", (e) => {
        const currentTarget = e.currentTarget as Element;
        currentMarkersDiv.forEach((e) => {
          e.classList.remove("active");
        });

        currentTarget.classList.add("active");
      });
    });
  }, [currentMarkersDiv]);

  /**
   * On lng, lat props change
   * This is trigger when the props lng and lat changed
   */
  useEffect(() => {
    const lngRound = roundLatLng(props.lng);
    const latRound = roundLatLng(props.lat);

    const sameCoordinate = lngRound === lng && latRound === lat;
    if (!isMounted.current || !map.current || sameCoordinate) return;

    setCoordinates({ lng: lngRound, lat: latRound });

    map.current.stop();

    const center: [number, number] = [
      lngRound,
      latFlyTo ? latFlyTo(latRound) : latRound,
    ];

    if (props.disableNextCenterAnimation) {
      // ✅ instant
      map.current.jumpTo({ center });
      props.onCenterApplied?.({ lng: lngRound, lat: latRound });
      return;
    }

    // ✅ animated (default)
    map.current.once("moveend", () => {
      props.onCenterApplied?.({
        lng: roundLatLng(map.current?.getCenter().lng),
        lat: roundLatLng(map.current?.getCenter().lat),
      });
    });

    map.current.flyTo({
      center,
      essential: true,
    });
  }, [props.lng, props.lat, props.disableNextCenterAnimation]);

  // Trigger a resize event
  useEffect(() => {
    if (map.current) {
      map.current.resize();
    }
  }, [props.resizeTrigger]);

  // Trigger a center event
  useEffect(() => {
    if (map.current) {
      map.current?.stop();
      map.current.flyTo({
        center: [coordinates.lng, coordinates.lat],
      });
    }
  }, [props.centerTrigger]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <MapBox ref={mapContainer} />
    </div>
  );
};
